import { Box, Grid, MenuItem, Select, Typography, Button, TextField, CircularProgress, Alert } from '@mui/material';
import { toast } from 'react-toastify';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { grey, blue } from '@mui/material/colors';
import { AuthContext } from '../../../context/logeduser';
import { useEmpLeaveRequest } from '../../../hooks/useLeaves';
import { marketHolidays } from '../marketholidays';
import { Label, SELECTED_MENU_PROPS } from '../../StyledComponents/StyledComponent';
import { useTheme } from '@mui/material/styles';
import { useGetSingleEmployee } from '../../../hooks/useEmployees';
import { GET_MANAGERS } from '../../../api';
import { useLoader } from '../../common/GlobalLoader';
import { leaveApi } from '../../Leave/leaveApi';

const Applied = ({ onClose, prefillDate }) => {
  const [display, setDisplay] = useState(true);
  const [status, setStatus] = useState({ type: null, msg: '' }); // Status for inline messages
  const [medicalFile, setMedicalFile] = useState(null);
  const [isModernWorkflowActive, setIsModernWorkflowActive] = useState(false);

  const { authData } = useContext(AuthContext);
  const id = authData?.employee_no;
  const { data: employeeDetails } = useGetSingleEmployee({ id });

  const theme = useTheme();

  const { mutate, isPending: isSubmitting } = useEmpLeaveRequest();
  const { showLoader, hideLoader } = useLoader();
  const submittingRef = useRef(false);
  // Synchronous local flag so the button disables on the SAME render as the click,
  // before React Query's isSubmitting has flipped. Without this, the button looks
  // clickable for a frame and users keep clicking.
  const [localSubmitting, setLocalSubmitting] = useState(false);

  // Drive the global backdrop directly from React Query's mutation state.
  // Declarative > imperative: this guarantees the loader hides whenever the
  // mutation settles, even if per-call callbacks don't fire (e.g. on unmount).
  useEffect(() => {
    if (isSubmitting) {
      showLoader('Submitting your leave request...');
    } else {
      hideLoader();
      submittingRef.current = false;
      setLocalSubmitting(false);
    }
  }, [isSubmitting, showLoader, hideLoader]);

  // Defensive: if this component unmounts while the loader is showing
  // (e.g. user navigates away mid-request), force-hide it.
  useEffect(() => {
    return () => hideLoader();
  }, [hideLoader]);

  // Master Data state
  const [managers, setManagers] = useState([]);

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const mgrs = await GET_MANAGERS();
        setManagers(Array.isArray(mgrs) ? mgrs : (mgrs?.managers || mgrs?.results || []));
      } catch (error) {
        console.error('Failed to fetch managers:', error);
        toast.error('Failed to load managers list');
      }
    };
    fetchMasterData();
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchLeaveWorkflowConfig = async () => {
      try {
        const cfg = await leaveApi.getConfig();
        if (mounted && cfg && cfg.is_modern_workflow_active === true) {
          setIsModernWorkflowActive(true);
        }
      } catch (_err) {
        // Fail closed to legacy leave behavior when config is not enabled or unreachable
      }
    };
    fetchLeaveWorkflowConfig();
    return () => {
      mounted = false;
    };
  }, []);


  // Manual manager entry toggle
  const [manualManager, setManualManager] = useState(false);
  const [manualManagerName, setManualManagerName] = useState('');

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0; // Sunday
  };
  const isHoliday = (date) => {
    const dateString = date.toISOString().split('T')[0];
    return marketHolidays.some(holiday => holiday.date === dateString);
  };



  const initialFormData = {
    employee_no: authData?.employee_no,
    leave_type: '',
    // Pre-filled to a single day when opened from the Attendance page (Absent
    // day click). Blank for a normal manual application.
    start_date: prefillDate || '',
    end_date: prefillDate || '',
    // Day Type defaults to Full Day (Session1 -> Session2). The single "Day Type"
    // dropdown drives these: First Half = S1->S1, Second Half = S2->S2.
    session_from: 'Session1',
    session_to: 'Session2',
    reason: '',
    leave_days: 0,
    recipient_name: '',
    recipient_email: '',
    // The backend adds the current tenant's configured HR mailbox.
    cc_emails: [],
  };
  const [formData, setFormData] = useState(initialFormData);

  // Opened from the Attendance page for a specific Absent day: pin the leave to
  // that single date and keep it in sync if the incoming date changes.
  useEffect(() => {
    if (prefillDate) {
      setFormData((prev) => ({ ...prev, start_date: prefillDate, end_date: prefillDate }));
    }
  }, [prefillDate]);

  // Handle input change for the Select and input fields
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Auto-populate Reporting Manager from employee details
  useEffect(() => {
    if (employeeDetails?.employee?.reporting_manager && !formData.recipient_name) {
      const assignedManager = employeeDetails.employee.reporting_manager;
      // The assigned manager name is the source of truth — set it even if the
      // managers list hasn't loaded (or failed), so the readonly field is never
      // blank and the employee can always submit. The list is used only to
      // enrich the exact email; if absent, the backend resolves it server-side
      // (ReportingManager.email -> name fallback).
      const matched = managers.find(m => m.name === assignedManager);
      setFormData(prev => ({
        ...prev,
        recipient_name: assignedManager,
        recipient_email: matched?.email || '',
      }));
    }
  }, [employeeDetails, managers, formData.recipient_name]);

  useEffect(() => {
    if (formData.start_date && formData.end_date) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);

      // Guard against an end-before-start range.
      if (endDate < startDate) {
        setFormData((prevData) => ({ ...prevData, leave_days: 0 }));
        return;
      }

      let numDays = 0;
      for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
        if (!isWeekend(date) && !isHoliday(date)) {
          numDays++;
        }
      }

      // Half-day handling. Convention: Session 1 = first half (morning),
      // Session 2 = second half (afternoon). A leave that STARTS in the
      // afternoon (session_from = Session2) loses the morning of its first day;
      // one that ENDS in the morning (session_to = Session1) loses the afternoon
      // of its last day — each is half a day. Only deduct when the boundary day
      // is itself a working day, so a range starting on a holiday isn't skewed.
      const startIsWorkday = !isWeekend(startDate) && !isHoliday(startDate);
      const endIsWorkday = !isWeekend(endDate) && !isHoliday(endDate);
      let days = numDays;
      if (formData.session_from === 'Session2' && startIsWorkday) days -= 0.5;
      if (formData.session_to === 'Session1' && endIsWorkday) days -= 0.5;
      if (days < 0) days = 0;

      setFormData((prevData) => ({
        ...prevData,
        leave_days: days,
      }));
    }
  }, [formData.start_date, formData.end_date, formData.session_from, formData.session_to]);

  const toggleInfo = () => {
    setDisplay(!display);
  };

  const handleSubmit = () => {
    // Synchronous re-entry guard — blocks rapid clicks within the same event loop
    // before React Query's isSubmitting state has a chance to flip on a re-render.
    if (submittingRef.current) return;

    // Determine manager name
    const effectiveManager = manualManager ? manualManagerName.trim() : formData.recipient_name;

    if (
      formData.leave_type === '' ||
      formData.start_date === '' ||
      formData.end_date === '' ||
      formData.session_from === '' ||
      formData.session_to === '' ||
      formData.reason === '' ||
      !effectiveManager
    ) {
      setStatus({ type: 'error', msg: 'Please fill all fields, including Reporting Manager.' });
      return;
    }

    // Same-day Session 2 → Session 1 is "afternoon to morning" — not a valid
    // span. (For a same-day leave: same session = half day; Session 1 → Session 2
    // = full day.)
    if (
      formData.start_date === formData.end_date &&
      formData.session_from === 'Session2' &&
      formData.session_to === 'Session1'
    ) {
      setStatus({ type: 'error', msg: 'For a same-day leave pick the same session for a half day, or Session 1 → Session 2 for a full day.' });
      return;
    }

    // Reject empty spans (e.g. only weekends/holidays selected, or end before start).
    if (!(formData.leave_days > 0)) {
      setStatus({ type: 'error', msg: 'Selected dates add up to 0 working days. Please check the dates and sessions.' });
      return;
    }

    // Medical certificate required for Sick Leave > 2 days only when modern workflow is active
    if (isModernWorkflowActive && formData.leave_type === 'Sick Leave' && formData.leave_days > 2 && !medicalFile) {
      setStatus({ type: 'error', msg: '❌ Medical certificate is required for Sick Leave exceeding 2 consecutive days.' });
      return;
    }

    // Frontend Validation
    if (employeeDetails?.employee) {
      const { employment_status, sick_leaves, casual_leaves, total_leaves } = employeeDetails.employee;
      const requestedDays = formData.leave_days;

      // 1. General Cap: Max 30 days per request
      if (requestedDays > 30) {
        setStatus({ type: 'error', msg: '❌ Leave request cannot exceed 30 days. Please contact HR for extended leave.' });
        return;
      }

      // 2. Balance Checks
      if (employment_status === 'Probation') {
        if (total_leaves < requestedDays) {
          setStatus({ type: 'error', msg: `❌ Insufficient balance. You only have ${total_leaves} days available in total.` });
          return;
        }
      } else if (employment_status === 'Confirmed') {
        if (formData.leave_type === 'Sick Leave') {
          if (sick_leaves < requestedDays) {
            setStatus({ type: 'error', msg: `❌ Not enough Sick Leave! You have ${sick_leaves} days available, but requested ${requestedDays} days.` });
            return;
          }
        } else if (formData.leave_type === 'Casual Leave' || formData.leave_type === 'On Duty' || formData.leave_type === 'Comp Off') {
          if (casual_leaves < requestedDays) {
            setStatus({ type: 'error', msg: `❌ Not enough Casual Leave! You have ${casual_leaves} days available, but requested ${requestedDays} days.` });
            return;
          }
        }
      }
    }

    const payload = {
      ...formData,
      recipient_name: effectiveManager,
      // Manual entry has no known mailbox — send blank so the backend resolves
      // it (ReportingManager.email -> name match) instead of a stale value.
      recipient_email: manualManager ? '' : formData.recipient_email,
    };

    // Lock submissions BEFORE the mutation fires.
    // - submittingRef: synchronous re-entry guard (blocks the function itself)
    // - setLocalSubmitting(true): triggers an immediate re-render so the button
    //   visibly disables in the same tick — users can't click again because
    //   the button is greyed out before any second click can register.
    submittingRef.current = true;
    setLocalSubmitting(true);

    let finalPayload = payload;
    if (isModernWorkflowActive) {
      const idempotencyKey = window.crypto?.randomUUID ? window.crypto.randomUUID() : `idemp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      if (medicalFile) {
        const data = new FormData();
        Object.entries(payload).forEach(([k, v]) => {
          if (v !== undefined && v !== null) {
            if (Array.isArray(v)) {
              v.forEach((item) => data.append(k, item));
            } else {
              data.append(k, v);
            }
          }
        });
        data.append('document', medicalFile);
        data.append('idempotency_key', idempotencyKey);
        finalPayload = data;
      } else {
        finalPayload = { ...payload, idempotency_key: idempotencyKey };
      }
    }

    mutate(
      { formData: finalPayload },
      {
        onSuccess: () => {
          setStatus({ type: 'success', msg: '✅ Leave applied! Closing...' });
          setMedicalFile(null);
          setTimeout(() => {
            if (onClose) onClose();
          }, 800);
          setFormData({ ...initialFormData, employee_no: authData?.employee_no });
        },
        onError: (error) => {
          const errorMsg = error?.response?.data?.detail || error?.message || 'Submission failed.';
          setStatus({ type: 'error', msg: `❌ ${errorMsg}` });
        },
      }
    );
  };

  const handleCancel = () => {
    setFormData({ ...initialFormData, employee_no: authData?.employee_no });
    setManualManager(false);
    setManualManagerName('');
    setMedicalFile(null);
    if (onClose) onClose();
  };

  // Single source of truth for "form is busy / locked" UI state
  const isBusy = localSubmitting || isSubmitting || status.type === 'success';

  const selectSx = {
    backgroundColor: theme.palette.mode === 'dark' ? grey[900] : '#fff',
    borderRadius: '8px',
    border: `1px solid ${theme.palette.mode === 'dark' ? grey[700] : grey[200]}`,
    boxShadow: `0px 2px 2px ${theme.palette.mode === 'dark' ? grey[900] : grey[50]}`,
    '&:hover': {
      '&& fieldset': {
        border: `1px solid ${blue[400]}`,
      },
    },
    '& .MuiSelect-select': {
      padding: '8px!important',
      height: '23px',
    },
    maxHeight: '39px',
  };

  return (
    <div>
      {/* Inline Status Message */}
      {status.type && (
        <Alert
          severity={status.type}
          sx={{ marginBottom: '16px', borderRadius: '8px' }}
          onClose={() => setStatus({ type: null, msg: '' })}
        >
          {status.msg}
        </Alert>
      )}

      {/* Info Box */}
      <Box
        sx={{
          backgroundColor: '#f0f6ff',
          border: '1px solid #dbe4ff',
          color: '#374151',
          display: display ? 'flex' : 'none',
          padding: '12px',
          borderRadius: '12px',
          marginBottom: '16px',
          fontSize: '0.875rem',
        }}
      >
        <span>
          Leave is earned by an employee and granted by the HR admin to take
          time off work. The employee is free to avail this leave in accordance
          with the company policy.
        </span>
      </Box>

      {/* Toggle Info Link */}
      <div style={{ display: 'flex', justifyContent: 'end', alignItems: 'end' }}>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a
          style={{ cursor: 'pointer', color: '#24a7f8', fontSize: '14px' }}
          onClick={toggleInfo}
        >
          {display ? 'Hide' : 'Info'}
        </a>
      </div>

      {/* Heading - hidden for modal use as modal title covers it */}
      {/* <Box sx={{ marginTop: '16px', marginBottom: '24px' }}>
        <Typography variant="h4">Applying for Leave</Typography>
      </Box> */}

      {/* Form Grid — locked while submission is in flight (defense in depth) */}
      <Grid
        container
        spacing={2.4}
        sx={{
          pointerEvents: isBusy ? 'none' : 'auto',
          opacity: isBusy ? 0.6 : 1,
          transition: 'opacity 0.2s ease',
        }}
      >

        {/* Regularising an Absent day from the Attendance page — the date is
            pre-filled and locked; the employee only picks the day type + reason. */}
        {prefillDate && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, px: 1.5, py: 1, borderRadius: '10px', bgcolor: '#eef4ff', border: '1px solid #dbe4ff', color: '#1e3a8a', fontSize: '0.85rem', fontWeight: 600 }}>
              📅 Applying leave for <b>{prefillDate}</b> — auto-filled from your attendance. Just pick <b>Full / Half day</b> and add a reason.
            </Box>
          </Grid>
        )}

        {/* Leave Type */}
        <Grid item xs={12} sm={3}>
          <Select
            sx={selectSx}
            name="leave_type"
            value={formData.leave_type}
            onChange={handleInputChange}
            fullWidth
            displayEmpty
            MenuProps={SELECTED_MENU_PROPS}
          >
            <MenuItem value="">Select Leave Type</MenuItem>
            <MenuItem
              value="Sick Leave"
              disabled={employeeDetails?.employee?.employment_status === 'Confirmed' && employeeDetails?.employee?.sick_leaves <= 0}
            >
              Sick Leave {employeeDetails?.employee?.employment_status === 'Confirmed' && `(${employeeDetails.employee.sick_leaves})`}
            </MenuItem>
            <MenuItem
              value="Casual Leave"
              disabled={employeeDetails?.employee?.employment_status === 'Confirmed' && employeeDetails?.employee?.casual_leaves <= 0}
            >
              Casual Leave {employeeDetails?.employee?.employment_status === 'Confirmed' && `(${employeeDetails.employee.casual_leaves})`}
            </MenuItem>
            <MenuItem value="On Duty">On Duty</MenuItem>
            <MenuItem value="Comp Off">Comp Off</MenuItem>
          </Select>
        </Grid>

        {/* From Date */}
        <Grid item xs={12} sm={3}>
          <TextField
            type="date"
            name="start_date"
            value={formData.start_date}
            onChange={prefillDate ? undefined : handleInputChange}
            InputProps={{ readOnly: !!prefillDate }}
            sx={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              border: `1px solid ${grey[200]}`,
              boxShadow: `0px 2px 2px ${grey[50]}`,
              '&:hover': { '&& fieldset': { border: `1px solid ${blue[400]}` } },
              '& .MuiInputBase-input': { padding: '8px !important', height: '23px' }
            }}
            fullWidth
          />
        </Grid>

        {/* To Date */}
        <Grid item xs={12} sm={3}>
          <TextField
            type="date"
            name="end_date"
            value={formData.end_date}
            onChange={prefillDate ? undefined : handleInputChange}
            InputProps={{ readOnly: !!prefillDate }}
            sx={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              border: `1px solid ${grey[200]}`,
              boxShadow: `0px 2px 2px ${grey[50]}`,
              '&:hover': { '&& fieldset': { border: `1px solid ${blue[400]}` } },
              '& .MuiInputBase-input': { padding: '8px !important', height: '23px' }
            }}
            fullWidth
          />
        </Grid>

        {/* Day Type — one clear control instead of a confusing from/to session
            range. Derives the backend session fields:
              Full Day    = Session1 -> Session2  (whole day, 1.0)
              First Half  = Session1 -> Session1  (morning only, 0.5)
              Second Half = Session2 -> Session2  (afternoon only, 0.5)
            For a multi-day range, First/Second Half trims a half day off the
            last/first day respectively. */}
        <Grid item xs={12} sm={3}>
          <Select
            sx={selectSx}
            name="day_type"
            value={
              formData.session_from === 'Session1' && formData.session_to === 'Session1'
                ? 'First'
                : formData.session_from === 'Session2' && formData.session_to === 'Session2'
                ? 'Second'
                : 'Full'
            }
            onChange={(e) => {
              const map = {
                Full: { session_from: 'Session1', session_to: 'Session2' },
                First: { session_from: 'Session1', session_to: 'Session1' },
                Second: { session_from: 'Session2', session_to: 'Session2' },
              };
              setFormData((prev) => ({ ...prev, ...map[e.target.value] }));
            }}
            fullWidth
            MenuProps={SELECTED_MENU_PROPS}
          >
            <MenuItem value="Full">Full Day</MenuItem>
            <MenuItem value="First">First Half (Morning)</MenuItem>
            <MenuItem value="Second">Second Half (Afternoon)</MenuItem>
          </Select>
        </Grid>

        {/* Reason */}
        <Grid item xs={12} sm={6}>
          <TextField
            name="reason"
            value={formData.reason}
            onChange={handleInputChange}
            placeholder="Enter the reason for leave"
            sx={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              border: `1px solid ${grey[200]}`,
              boxShadow: `0px 2px 2px ${grey[50]}`,
              '&:hover': { '&& fieldset': { border: `1px solid ${blue[400]}` } },
              '& .MuiInputBase-input': { padding: '8px !important', height: '23px' }
            }}
            fullWidth
          />
        </Grid>

        {/* Medical Document Upload (Mandatory for Sick Leave > 2 days in modern workflow) */}
        {isModernWorkflowActive && formData.leave_type === 'Sick Leave' && formData.leave_days > 2 && (
          <Grid item xs={12}>
            <Box sx={{ p: 2, borderRadius: '12px', bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e', mb: 1 }}>
                📎 Medical Certificate (Required for Sick Leave &gt; 2 Days)
              </Typography>
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => setMedicalFile(e.target.files[0] || null)}
                id="medical-certificate-upload"
                style={{ display: 'none' }}
              />
              <label htmlFor="medical-certificate-upload">
                <Button
                  variant="outlined"
                  component="span"
                  size="small"
                  sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#d97706', color: '#b45309' }}
                >
                  {medicalFile ? `Selected: ${medicalFile.name}` : 'Upload Medical Certificate'}
                </Button>
              </label>
              {medicalFile && (
                <Typography variant="caption" sx={{ ml: 1.5, color: '#166534', fontWeight: 600 }}>
                  ✓ {medicalFile.name} ({(medicalFile.size / 1024).toFixed(1)} KB)
                </Typography>
              )}
            </Box>
          </Grid>
        )}

        {/* Live day total — makes a half-day (0.5) visible before submit. */}
        {formData.start_date && formData.end_date && (
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ color: formData.leave_days > 0 ? '#2f6bff' : '#dc2626', fontWeight: 700 }}>
              Total: {formData.leave_days} working day{formData.leave_days === 1 ? '' : 's'}
              {formData.leave_days % 1 !== 0 && ' (includes a half day)'}
            </Typography>
          </Grid>
        )}

        {/* ───── Reporting Manager ───── */}
        <Grid item xs={12} sm={4}>
          <Label>Reporting Manager</Label>
          {employeeDetails?.employee?.reporting_manager ? (
            <TextField
              sx={{
                ...selectSx,
                backgroundColor: theme.palette.mode === 'dark' ? grey[900] : grey[100],
                '& .MuiInputBase-input': { padding: '8px !important', height: '23px', cursor: 'default' }
              }}
              value={formData.recipient_name}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          ) : !manualManager ? (
            <>
              <Select
                sx={selectSx}
                name="recipient_name"
                value={formData.recipient_name}
                onChange={(e) => {
                  if (e.target.value === '__manual__') {
                    setManualManager(true);
                    setFormData(prev => ({ ...prev, recipient_name: '', recipient_email: '' }));
                  } else {
                    const picked = managers.find(m => m.name === e.target.value);
                    setFormData(prev => ({
                      ...prev,
                      recipient_name: e.target.value,
                      recipient_email: picked?.email || '',
                    }));
                  }
                }}
                fullWidth
                displayEmpty
                MenuProps={SELECTED_MENU_PROPS}
              >
                <MenuItem value="">Select Manager</MenuItem>
                {managers.map((mgr) => (
                  <MenuItem key={mgr.id} value={mgr.name}>{mgr.name}</MenuItem>
                ))}
                <MenuItem value="__manual__" sx={{ fontStyle: 'italic', color: blue[600] }}>
                  Enter manually…
                </MenuItem>
              </Select>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Manager name"
                value={manualManagerName}
                onChange={(e) => setManualManagerName(e.target.value)}
                sx={{
                  flex: 1,
                  '& .MuiInputBase-input': { padding: '8px !important', height: '23px' }
                }}
              />
              <Button
                size="small"
                variant="text"
                onClick={() => { setManualManager(false); setManualManagerName(''); }}
                sx={{ minWidth: 'auto', fontSize: '0.75rem' }}
              >
                ← List
              </Button>
            </Box>
          )}
        </Grid>

        {/* ───── CC: fixed to the HR mailbox (always CC'd, not selectable) ───── */}
        <Grid item xs={12} sm={4}>
          <Label>CC</Label>
          <Box
            sx={{
              px: 1.75,
              minHeight: 44,
              display: 'flex',
              alignItems: 'center',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              bgcolor: '#f8fafc',
              color: '#374151',
              fontSize: '0.9rem',
            }}
          >
            hr@adityatrading.com
          </Box>
        </Grid>

        {/* Submit and Cancel Buttons — kept interactive (re-enabled by pointer-events override)
            so the visual disabled state is what blocks input, not the parent Grid lock. */}
        <Grid
          item
          xs={12}
          sm={12}
          sx={{
            textAlign: 'right',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.5,
            alignItems: 'center',
            pointerEvents: 'auto',
            opacity: 1,
          }}
        >
          {isBusy && status.type !== 'success' && (
            <Typography variant="body2" sx={{ mr: 1, color: '#2f6bff', fontWeight: 600 }}>
              Submitting your leave request...
            </Typography>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isBusy}
            startIcon={isBusy && status.type !== 'success' ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              minWidth: 180,
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '12px',
              background: isBusy ? undefined : 'linear-gradient(135deg, #3b82f6 0%, #2f6bff 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2f6bff 0%, #2557d6 100%)',
              },
              '&.Mui-disabled': {
                color: '#fff',
                background: status.type === 'success'
                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
              },
            }}
          >
            {status.type === 'success'
              ? '✓ Submitted'
              : isBusy
                ? 'Submitting Leave...'
                : 'Submit Leave'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={isBusy}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' }}
          >
            Cancel
          </Button>
        </Grid>

      </Grid>
    </div>
  );
};

export default Applied;
